import { markdownLanguage } from "@codemirror/lang-markdown";

export const EMPTY_TABLE = "| Column 1 | Column 2 |\n| -------- | -------- |\n|          |          |";

export type TableAlignment = "default" | "left" | "center" | "right";

export interface MarkdownTable {
  from: number;
  to: number;
  prefix: string;
  headers: string[];
  alignments: TableAlignment[];
  rows: string[][];
}

export interface TableChange {
  from: number;
  to: number;
  insert: string;
}

export type TableAction =
  | { type: "edit"; row: number; column: number; value: string }
  | { type: "add-row" }
  | { type: "add-column" }
  | { type: "delete-column"; column: number }
  | { type: "move-row"; from: number; to: number }
  | { type: "move-column"; from: number; to: number };

interface TableNode {
  name: string;
  from: number;
  to: number;
  firstChild: TableNode | null;
  nextSibling: TableNode | null;
}

function children(node: TableNode): TableNode[] {
  const result: TableNode[] = [];
  for (let child = node.firstChild; child; child = child.nextSibling) result.push(child);
  return result;
}

function cellTexts(row: TableNode, source: string): string[] {
  const rowChildren = children(row);
  const delimiters = rowChildren.filter((child) => child.name === "TableDelimiter");
  const boundaries = [row.from, ...delimiters.flatMap((child) => [child.from, child.to]), row.to];
  const cells: string[] = [];
  for (let index = 0; index < boundaries.length - 1; index += 2) {
    const from = boundaries[index]!;
    const to = boundaries[index + 1]!;
    if (from === to) continue;
    const cell = rowChildren.find((child) => child.name === "TableCell"
      && child.from >= from && child.to <= to);
    cells.push(cell ? source.slice(cell.from, cell.to).trim() : "");
  }
  return cells;
}

function alignment(cell: string): TableAlignment {
  const marker = cell.trim();
  if (marker.startsWith(":") && marker.endsWith(":")) return "center";
  if (marker.startsWith(":")) return "left";
  if (marker.endsWith(":")) return "right";
  return "default";
}

export function parseMarkdownTables(source: string): MarkdownTable[] {
  const result: MarkdownTable[] = [];
  const tree = markdownLanguage.parser.parse(source);
  tree.iterate({
    enter(node) {
      if (node.name !== "Table") return;
      const tableChildren = children(node.node);
      const header = tableChildren.find((child) => child.name === "TableHeader");
      const separator = tableChildren.find((child) => child.name === "TableDelimiter");
      if (!header || !separator) return false;
      const headers = cellTexts(header, source);
      const separatorText = source.slice(separator.from, separator.to).trim()
        .replace(/^\|/, "").replace(/\|$/, "");
      const alignments = separatorText.split("|").map(alignment);
      const lineStart = source.lastIndexOf("\n", node.from - 1) + 1;
      result.push({
        from: node.from,
        to: node.to,
        prefix: source.slice(lineStart, node.from),
        headers,
        alignments: headers.map((_, index) => alignments[index] ?? "default"),
        rows: tableChildren.filter((child) => child.name === "TableRow")
          .map((row) => {
            const cells = cellTexts(row, source);
            return headers.map((_, index) => cells[index] ?? "");
          }),
      });
      return false;
    },
  });
  return result;
}

export function tableAtPosition(source: string, position: number): MarkdownTable | null {
  return parseMarkdownTables(source)
    .find((table) => table.from <= position && position <= table.to) ?? null;
}

function pad(value: string, width: number): string {
  const length = Array.from(value).length;
  return value + " ".repeat(Math.max(0, width - length));
}

export function serializeMarkdownTable(table: MarkdownTable): string {
  const columns = table.headers.length;
  const widths = table.headers.map((header, index) => Math.max(
    3,
    Array.from(header).length,
    ...table.rows.map((row) => Array.from(row[index] ?? "").length),
  ));
  const row = (cells: readonly string[]): string => `| ${widths.map((width, index) =>
    pad(cells[index] ?? "", width)).join(" | ")} |`;
  const separator = widths.map((width, index) => {
    const align = table.alignments[index] ?? "default";
    if (align === "left") return `:${"-".repeat(width - 1)}`;
    if (align === "center") return `:${"-".repeat(width - 2)}:`;
    if (align === "right") return `${"-".repeat(width - 1)}:`;
    return "-".repeat(width);
  });
  return [row(table.headers), row(separator), ...table.rows.map(row)]
    .join(`\n${table.prefix}`);
}

export function tableFormatChanges(
  source: string,
  selection?: { from: number; to: number },
): TableChange[] {
  return parseMarkdownTables(source).filter((table) => {
    if (!selection) return true;
    if (selection.from === selection.to) {
      return table.from <= selection.from && selection.from <= table.to;
    }
    return table.from < selection.to && table.to > selection.from;
  }).map((table) => ({
    from: table.from,
    to: table.to,
    insert: serializeMarkdownTable(table),
  })).filter((change) => source.slice(change.from, change.to) !== change.insert);
}

function move<T>(values: T[], from: number, to: number): void {
  if (from < 0 || from >= values.length || to < 0 || to >= values.length || from === to) return;
  values.splice(to, 0, values.splice(from, 1)[0]!);
}

function escapeCell(value: string): string {
  return value.replace(/\r?\n/g, " ").replace(/\\\|/g, "|").replace(/\|/g, "\\|").trim();
}

export function tableActionChange(
  source: string,
  tableFrom: number,
  action: TableAction,
): TableChange | null {
  const table = parseMarkdownTables(source).find((candidate) => candidate.from === tableFrom);
  if (!table) return null;
  const columns = table.headers.length;
  if (action.type === "edit") {
    if (action.column < 0 || action.column >= columns) return null;
    const row = action.row === -1 ? table.headers : table.rows[action.row];
    if (!row) return null;
    row[action.column] = escapeCell(action.value);
  } else if (action.type === "add-row") {
    table.rows.push(Array(columns).fill(""));
  } else if (action.type === "add-column") {
    table.headers.push(`Column ${columns + 1}`);
    table.alignments.push("default");
    for (const row of table.rows) row.push("");
  } else if (action.type === "delete-column") {
    if (columns <= 1 || !Number.isInteger(action.column) || action.column < 0 || action.column >= columns) return null;
    table.headers.splice(action.column, 1);
    table.alignments.splice(action.column, 1);
    for (const row of table.rows) row.splice(action.column, 1);
  } else if (action.type === "move-row") {
    move(table.rows, action.from, action.to);
  } else {
    move(table.headers, action.from, action.to);
    move(table.alignments, action.from, action.to);
    for (const row of table.rows) move(row, action.from, action.to);
  }
  const insert = serializeMarkdownTable(table);
  return source.slice(table.from, table.to) === insert
    ? null : { from: table.from, to: table.to, insert };
}

/** Keep a new table separate from adjacent paragraphs, including when inserted mid-line. */
/** The line breaks a block put between `from` and `to` needs to stand apart, a blank line either side. */
export function blockBreaks(document: string, from: number, to: number = from): { prefix: string; suffix: string } {
  const before = document.slice(0, from);
  const after = document.slice(to);
  const prefix = before.endsWith("\n\n") || before === ""
    ? ""
    : before.endsWith("\n") ? "\n" : "\n\n";
  const suffix = after.startsWith("\n\n") || after === ""
    ? ""
    : after.startsWith("\n") ? "\n" : "\n\n";
  return { prefix, suffix };
}

export function tableInsertion(
  document: string,
  from: number,
  to: number = from,
): { text: string; selectFrom: number; selectTo: number } {
  const { prefix, suffix } = blockBreaks(document, from, to);
  return {
    text: `${prefix}${EMPTY_TABLE}${suffix}`,
    selectFrom: prefix.length + 2,
    selectTo: prefix.length + 10,
  };
}
